import './feature-guide-billboard.scss';
import React, { useCallback, useMemo } from 'react';
import { DyfFeatureGuideContentProps } from '../../../../views/dyf-landing-view/hooks/use-dyf-feature-guide-content';
import { useSearchContent } from '../../search-bar/hooks/use-search-content';
import { SignInReplaceComponent } from '../sign-in-replace-component/sign-in-replace-component';
import { ProfileWithVehiclesResponse } from '../../../../models/profile-with-vehicles';

import { EligibleModelData } from '../../../../hooks/dyf/use-dyf-eligible-model/use-dyf-eligible-model';
import {
    AuthenticationState,
    useAuthentication,
} from '../../../../hooks/use-authentication';
import { useTrimSpecSelector } from '../../../../hooks/dyf/use-trim-spec-selector/use-trim-spec-selector';
import { useBillboardClassNames } from '../../../../hooks/dyf/use-billboard-class-names/use-billboard-class-names';
import { SearchOrSpecWrap } from './feature-guide-billboard-search-or-spec-wrap/search-or-spec-wrap';

import { useVehicleSelectorToggle } from '../../../../hooks/dyf/use-vehicle-selector-toggle/use-vehicle-selector-toggle';
import { OnboardingVehicleSelectorButton } from './vehicle-selector-button/vehicle-selector-button';
import { FMDropdown } from '@own/fds-react';

interface BillboardProps {
    eligibleFeatureGuide?: boolean;
    eligibleModelData: EligibleModelData | null;
    vin: string | null;
    featureGuideContent: DyfFeatureGuideContentProps;
    flow: 'ymm' | 'vin';
    profile: ProfileWithVehiclesResponse | undefined;
    vehicleImage: string | null;
    isMobile?: boolean;
}

export const onboardBillboardReplaceYMM = (
    data: string,
    eligibleModel: EligibleModelData | null
): string => {
    return data
        ?.replace('[year]', eligibleModel?.modelYear || '')
        .replace('[make]', eligibleModel?.modelMakeDisplayName || '')
        ?.replace('[model]', eligibleModel?.modelDisplayName || '');
};

export const FeatureGuideBillboard = (props: BillboardProps) => {
    const {
        eligibleModelData,
        eligibleFeatureGuide,
        featureGuideContent,
        flow,
        vehicleImage,
    } = props;
    const shouldDisplayVehicleSelector = useVehicleSelectorToggle();
    const searchContent = useSearchContent();

    const {
        selectedTrim,
        selectedSpecs,
        handleTrimDropdownChange,
        trimDescriptionList,
    } = useTrimSpecSelector(eligibleModelData, flow);

    const [authState, loginFromHook] = useAuthentication();

    const wrapperClassNames = useBillboardClassNames(vehicleImage);

    const shouldShowTrimSelector = useMemo(() => {
        if (flow === 'ymm') {
            return true;
        } else return false;
    }, [flow]);

    const login = useCallback(() => {
        loginFromHook();
    }, [loginFromHook]);

    const handleDropdownChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ): void => {
        handleTrimDropdownChange(event.target.value);
    };

    const billboardText = useMemo(
        () =>
            flow === 'ymm' ? (
                <SignInReplaceComponent
                    description={
                        authState === AuthenticationState.Authenticated
                            ? featureGuideContent.ymmBillboardAuthDescription
                            : featureGuideContent.ymmBillboardUnauthDescription
                    }
                    handleLogin={login}
                />
            ) : (
                <div
                    className="billboard-body-text"
                    data-testid="billboard-body-text-vin"
                >
                    <p>{featureGuideContent?.billboardBodyText}</p>
                </div>
            ),
        [flow, authState, featureGuideContent]
    );

    const vehicleImageAlt = useMemo(() => {
        if (eligibleModelData) {
            return `${eligibleModelData.modelYear} ${eligibleModelData.modelMakeDisplayName} ${eligibleModelData.modelDisplayName} front view`;
        }
        return null;
    }, [eligibleModelData]);

    return (
        <div className={wrapperClassNames}>
            <div className="billboard-text-wrapper">
                <div className="vehicle-title" data-testid="vehicle-title">
                    <h1>
                        {onboardBillboardReplaceYMM(
                            featureGuideContent?.billboardTitle,
                            eligibleModelData
                        )}
                    </h1>
                </div>
                <div
                    className="billboard-body-text"
                    data-testid="billboard-body-text"
                >
                    {billboardText}
                </div>
            </div>

            {shouldShowTrimSelector && (
                <div
                    className="onboarding-billboard-ymm-trim-selector"
                    style={{ maxWidth: '320px' }}
                >
                    <FMDropdown
                        ariaLabel={featureGuideContent.trimDropDownAriaLabel}
                        options={trimDescriptionList}
                        header={featureGuideContent.trimDropDownAriaLabel}
                        value={selectedTrim ?? ''}
                        onChange={handleDropdownChange}
                        dataTestId="feature-guide-trim-dropdown"
                    ></FMDropdown>
                </div>
            )}

            <div
                className={`billboard-vehicle-image--wrap ${
                    flow == 'ymm' ? 'hidden' : ''
                }`}
            >
                {vehicleImage && (
                    <img
                        src={vehicleImage}
                        alt={vehicleImageAlt || ''}
                        data-testid="billboard-vehicle-image"
                    />
                )}
            </div>

            <SearchOrSpecWrap
                flow={flow}
                eligibleFeatureGuide={eligibleFeatureGuide?.valueOf() ?? false}
                selectedSpecs={selectedSpecs}
                selectedTrim={selectedTrim}
                searchContent={searchContent}
                shouldShowTrimSelector={shouldShowTrimSelector}
            />
            <div className="desktop-spacer"></div>
            {shouldDisplayVehicleSelector && (
                <>
                    <OnboardingVehicleSelectorButton
                        buttonText={
                            featureGuideContent.vehicleSelectorButtonText
                        }
                        className="selector-desktop"
                        lowercaseBrand={'ford'}
                    />
                    <div className={'desktop-spacer'}></div>
                </>
            )}
        </div>
    );
};
